import { useCallback, useEffect, useRef, useState } from 'react'
import { uploadVideo } from 'lib/cloudinary/upload-video'
import { MAX_VIDEOS } from './utils'

export const UPLOAD_STATUS = {
  UPLOADING: 'uploading',
  UPLOADED: 'uploaded',
  ERROR: 'error',
}

export const UPLOAD_ERROR_MESSAGE =
  'Upload failed. Check your connection and try again.'

let nextItemId = 0

/**
 * Tracks up to `max` videos the golfer picked. Each file starts uploading to
 * Cloudinary as soon as it is added, so progress, retry, and remove are per
 * file. The uploaded URLs stay in state, so a failed submit can be retried
 * without uploading again.
 *
 * `onEvent(name, attributes)` reports analytics (`selected`, `failed`).
 * `initialItems` seeds the list (used by the dev preview).
 */
const useVideoUploads = ({
  max = MAX_VIDEOS,
  onEvent,
  initialItems = [],
} = {}) => {
  const [items, setItems] = useState(initialItems)
  const itemsRef = useRef(items)
  itemsRef.current = items
  const controllersRef = useRef(new Map())
  const isMountedRef = useRef(true)

  useEffect(() => {
    isMountedRef.current = true
    const controllers = controllersRef.current
    return () => {
      isMountedRef.current = false
      controllers.forEach(controller => controller.abort())
      controllers.clear()
    }
  }, [])

  const update = useCallback((id, patch) => {
    if (!isMountedRef.current) return
    setItems(previous =>
      previous.map(item => (item.id === id ? { ...item, ...patch } : item)),
    )
  }, [])

  const startUpload = useCallback(
    async item => {
      const controller = new AbortController()
      controllersRef.current.set(item.id, controller)
      update(item.id, {
        status: UPLOAD_STATUS.UPLOADING,
        progress: 0,
        error: null,
      })
      try {
        const { url } = await uploadVideo(item.file, {
          folder: 'virtual-coaching',
          signal: controller.signal,
          onProgress: progress => update(item.id, { progress }),
        })
        update(item.id, { status: UPLOAD_STATUS.UPLOADED, progress: 100, url })
      } catch (error) {
        if (error?.name === 'AbortError') return
        update(item.id, {
          status: UPLOAD_STATUS.ERROR,
          error: UPLOAD_ERROR_MESSAGE,
        })
        onEvent?.('failed', { size: item.file.size })
      } finally {
        controllersRef.current.delete(item.id)
      }
    },
    [update, onEvent],
  )

  // Returns a message when some picked files were not added.
  const addFiles = useCallback(
    files => {
      const room = Math.max(max - itemsRef.current.length, 0)
      const accepted = files.slice(0, room)
      const newItems = accepted.map(file => ({
        id: `video-${(nextItemId += 1)}`,
        file,
        status: UPLOAD_STATUS.UPLOADING,
        progress: 0,
        url: null,
        error: null,
      }))
      if (newItems.length > 0) {
        setItems(previous => [...previous, ...newItems])
        newItems.forEach(startUpload)
        onEvent?.('selected', { count: newItems.length })
      }
      return accepted.length < files.length
        ? `You can add up to ${max} videos. Remove one to add another.`
        : null
    },
    [max, startUpload, onEvent],
  )

  const remove = useCallback(id => {
    controllersRef.current.get(id)?.abort()
    controllersRef.current.delete(id)
    setItems(previous => previous.filter(item => item.id !== id))
  }, [])

  const retry = useCallback(
    id => {
      const item = itemsRef.current.find(candidate => candidate.id === id)
      if (item) startUpload(item)
    },
    [startUpload],
  )

  const uploadedItems = items.filter(
    item => item.status === UPLOAD_STATUS.UPLOADED,
  )

  return {
    items,
    addFiles,
    remove,
    retry,
    uploadedUrls: uploadedItems.map(item => item.url),
    isUploading: items.some(item => item.status === UPLOAD_STATUS.UPLOADING),
    hasError: items.some(item => item.status === UPLOAD_STATUS.ERROR),
    isReady: items.length > 0 && uploadedItems.length === items.length,
    isFull: items.length >= max,
  }
}

export default useVideoUploads
